import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo, useCallback } from 'react'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'

import type { Floorplan } from '@/types/api'
import { fetchFloorplans, deleteFloorplan } from '@/api/floorplans.api'
import { buildColumns } from './columns'
import { FloorplanFilterPanel } from './FloorplanFilterPanel'
import type { FloorplanFilterValues } from './FloorplanFilterPanel'
import { FloorplanDetailDrawer } from './FloorplanDrawer'
import { FloorplanFormDrawer } from './FloorplanFormDrawer'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { usePermission } from '@/hooks/use-permission'
import { downloadFileWithName } from '@/utils/download'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportFloorPlans, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: FloorplanFilterValues = {
  keySearch: '',
  unitId: undefined,
  unitName: undefined,
  time: undefined,
}

export function FloorplansPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const canCreate = usePermission('diagram-create')
  const canView = usePermission('diagram-view')
  const canUpdate = usePermission('diagram-update')
  const canDelete = usePermission('diagram-delete')
  const canExport = usePermission('diagram-export')

  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<FloorplanFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<FloorplanFilterValues>(DEFAULT_FILTER)

  // States cho Modals/Drawers
  const [viewingPlan, setViewingPlan] = useState<Floorplan | null>(null)
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingPlan, setEditingPlan] = useState<Floorplan | null>(null)
  const [deletingPlan, setDeletingPlan] = useState<Floorplan | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  const queryClient = useQueryClient()

  const resolvedUnitId = showUnitColumn
    ? activeFilters.unitId
    : currentUser?.unitId

  const apiParams = useMemo(() => {
    return {
      keySearch: activeFilters.keySearch || undefined,
      unitId: resolvedUnitId,
      fromDate: formatStartDate(activeFilters.time?.from) || undefined,
      toDate: formatEndDate(activeFilters.time?.to) || undefined,
    }
  }, [activeFilters, resolvedUnitId])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['floorplans', pageIndex, pageSize, apiParams],
    queryFn: ({ signal }) =>
      fetchFloorplans(pageIndex, pageSize, apiParams, signal),
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const hasData = Boolean(data?.items && data.items.length > 0)

  const { mutate: removeFloorplan, isPending: isRemoving } = useMutation({
    mutationFn: (floorplan: Floorplan) => deleteFloorplan(floorplan.id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Xóa sơ đồ trung tâm thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['floorplans'] })
    },
    onSettled: () => {
      setDeletingPlan(null)
    },
  })

  const confirmDelete = (floorplan: Floorplan) => {
    setDeletingPlan(floorplan)
  }

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        showUnitColumn,
        canView,
        canUpdate,
        canDelete,
        onViewDetail: (floorplan) => setViewingPlan(floorplan),
        onUpdate: (floorplan) => {
          setFormMode('update')
          setEditingPlan(floorplan)
          setIsFormDrawerOpen(true)
        },
        onDelete: confirmDelete,
      }),
    [pageIndex, pageSize, showUnitColumn, canView, canUpdate, canDelete],
  )

  // Callback gọi API xuất file Excel sơ đồ trung tâm
  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      try {
        setIsExporting(true)
        await exportFloorPlans(
          {
            page: pageIndex,
            size: pageSize,
            ...apiParams,
          },
          payload,
          'Danh_sach_so_do_trung_tam',
        )
      } finally {
        setIsExporting(false)
      }
    },
    [pageIndex, pageSize, apiParams],
  )

  const { handleExport } = useTableExport(
    'Danh sách sơ đồ trung tâm',
    columns,
    handleExportApi,
  )

  const handleDownload = async (url: string, fileName?: string) => {
    try {
      await downloadFileWithName({ url, fileName })
    } catch (error) {
      window.open(url, '_blank')
    }
  }

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH SƠ ĐỒ TRUNG TÂM" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH SƠ ĐỒ TRUNG TÂM"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!hasData}
        isExporting={isExporting}
      >
        {canCreate && (
          <Button
            variant="primary"
            size="md"
            className="flex-1 gap-2 sm:flex-none h-10"
            onClick={() => {
              setFormMode('create')
              setEditingPlan(null)
              setIsFormDrawerOpen(true)
            }}
          >
            <Plus size={16} />
            Thêm mới
          </Button>
        )}
      </PageHeader>

      {showFilter && (
        <FloorplanFilterPanel
          values={filter}
          onChange={setFilter}
          showUnitColumn={showUnitColumn}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              keySearch: filter.keySearch.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        tableOptions={{
          state: {
            pagination: { pageIndex, pageSize },
          },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) =>
              setPagination((s) => ({ ...s, pageIndex: p - 1 }))
            }
            onPageSizeChange={(size) =>
              setPagination({ pageIndex: 0, pageSize: size })
            }
          />
        }
      />

      <FloorplanDetailDrawer
        floorplanId={viewingPlan?.id || null}
        onClose={() => setViewingPlan(null)}
        showUnitColumn={showUnitColumn}
        onDownload={handleDownload}
        onUpdate={(plan) => {
          setViewingPlan(null)
          setFormMode('update')
          setEditingPlan(plan)
          setIsFormDrawerOpen(true)
        }}
      />

      {isFormDrawerOpen && (
        <FloorplanFormDrawer
          isOpen={isFormDrawerOpen}
          mode={formMode}
          floorplan={editingPlan}
          onClose={() => {
            setIsFormDrawerOpen(false)
            setEditingPlan(null)
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deletingPlan}
        title="Xóa sơ đồ"
        variant="danger"
        description={
          <>
            Khi sơ đồ bị xóa, các quầy được gán với sơ đồ cũng sẽ bị xóa khỏi hệ thống.
            <br />
            Bạn có chắc chắn muốn xóa sơ đồ{' '}
            <strong>{deletingPlan?.name}</strong> này?
          </>
        }
        isLoading={isRemoving}
        onClose={() => setDeletingPlan(null)}
        onConfirm={() => deletingPlan && removeFloorplan(deletingPlan)}
      />
    </section>
  )
}
